import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import ChangePasswordPrompt from 'components/admin/ChangePasswordPrompt';
import { FieldControl, SectionTopTitle } from 'components/base/CommonComponent';
import InputFieldControl from 'components/base/InputFieldControl';
import { CustomTitleLabel } from 'components/earning/CustomBaseComponments';
import { ADMINISTRATOR_TYPES } from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';
import { AdminErrorHandleFields } from 'containers/admin/admins/AdminErrorHandleFields';
import { ToastType } from 'models/NavBarModel';

import { createAction } from 'utils';
import BaseMultipleSelectorV2 from 'components/base/BaseMultipleSelectorV2';
import RedirectToStorePrompt from 'components/transactions/RedirectToStorePrompt';
import { ReactHookFormErrorMessage } from 'components/base/ErrorFieldMessage';

export const ADMINISTRATOR_TYPES_CONFIG = [
  {
    label: 'CMS Admin Users',
    value: ADMINISTRATOR_TYPES.CMS_ADMINISTRATOR,
  },
  {
    label: 'Merchant App Users',
    value: ADMINISTRATOR_TYPES.MERCHANT_APP_ACCOUNT,
  },
];

const ChangePasswordCom = ({ changeAction }) => {
  return (
    <div className="d-flex flex-column">
      <CustomTitleLabel title="Password" />
      <label className="tips-message">
        Raw passwords are not stored, so there is no way to see this user's
        password.
      </label>
      <label className="tips-message">{'*'.repeat(10)}</label>
      <Button className="btn-back-button-common" onClick={changeAction}>
        Change
      </Button>
    </div>
  );
};

const mapPropsToState = (state) => ({
  stores: state.storeModel.pagedStoreList,
  adminName: state.admin.oneAdmin.name,
})
function GeneralSections({
  stores,
  adminName,
  adminType,
}) {
  const params = useParams();
  const { id } = params;
  const [showChangePassPrompt, setShowChangePassPrompt] = useState(false);
  const { watch, setValue, formState: { errors } } = useFormContext();
  const merchantStoresField = "merchantStores"
  const watchName = watch('name');
  const watchPassword = watch('password');
  const watchConfirmPassword = watch('confirmPassword');
  const watchAdminType = adminType;
  const watchMerchantStores = watch(merchantStoresField) || []
  const dispatch = useDispatch();
  const [showCreateStorePrompt, setShowCreateStorePrompt] = useState(false);
  const lastRefreshDateRef = useRef(new Date())
  const [refreshDate, setRefreshDate] = useState(lastRefreshDateRef.current);
  useEffect(() => {
    if (watchAdminType === ADMINISTRATOR_TYPES.MERCHANT_APP_ACCOUNT ) {
      dispatch(
        createAction('storeModel/getPagedStoreWithBrand')({}),
      );
    }
    return () => {
      dispatch(
        createAction('storeModel/clearData')({}),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchAdminType]);

  useEffect(()=> {
    if (refreshDate !== lastRefreshDateRef.current) {
      dispatch(
        createAction('storeModel/getPagedStoreWithBrand')({ isRefresh: true}),
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshDate])

  const passwordRex = /\D/g;
  const nameSection = (
    <InputFieldControl
      name={'name'}
      title={'Name'}
      rules={{ required: AdminErrorHandleFields.required.username }}
      value={watchName}
      setValue={(value) => {
        setValue('name', value, { shouldDirty: true });
      }}
    />
  );
  const passwordSection = (
    <InputFieldControl
      name={'password'}
      title={'Password'}
      autoComplete="new-password"
      tips="Password at least 8 characters, can't be entirely numeric."
      type="password"
      rules={{
        validate: {
          lessThanEight: (value) => {
            return (
              value?.length >= 8 ||
              AdminErrorHandleFields.others.password.length
            );
          },
          allNumeric: (value) => {
            return (
              passwordRex.test(value) ||
              AdminErrorHandleFields.others.password.numeric
            );
          },
        },
      }}
      value={watchPassword}
      setValue={(value) => {
        setValue('password', value, { shouldDirty: true });
      }}
    />
  );
  const confirmPasswordSection = (
    <InputFieldControl
      name={'confirmPassword'}
      title={'Password confirmation'}
      tips="Enter the same password as before, for verification"
      type="password"
      autoComplete="new-password"
      rules={{
        validate: {
          lessThanEight: (value) => {
            return (
              value?.length ||
              AdminErrorHandleFields.required.passwordConfirmation
            );
          },
          allNumeric: (value) => {
            if (watchPassword !== value) {
              setValue('password', '', { shouldDirty: true });
              setValue('confirmPassword', '', { shouldDirty: true });
              dispatch({
                type: 'navBars/updateState',
                payload: {
                  saveDiscardToastShowing: {
                    value: true,
                    type: ToastType.passwordConfirmation,
                  },
                },
              });
            }
            return (
              watchPassword === value ||
              'Confirmation password should be same with password entered before.'
            );
          },
        },
      }}
      value={watchConfirmPassword}
      setValue={(value) => {
        setValue('confirmPassword', value, { shouldDirty: true });
      }}
    />
  );
  const convertAdminType = (adminType) => {
    if (!adminType) {
      return '-'
    }
    const displayAdminType = ADMINISTRATOR_TYPES_CONFIG.find((item) => item.value === adminType)
    return displayAdminType?.label || '-'
  };
  return (
    <>
      <SectionTopTitle title="General" />
      {nameSection}
      {id ? (
        <div className="d-flex flex-column">
          <CustomTitleLabel title="Users type" />
          <label className="tips-message">
            Once set, cannot be changed.
          </label>
          <label className="custom-record-normal-font custom-timeline-time-color list-new-line">
            {convertAdminType(watchAdminType)}
          </label>
        </div>
      ) : null}

      {watchAdminType === ADMINISTRATOR_TYPES.MERCHANT_APP_ACCOUNT ? (
        <>
          <FieldControl
            name={merchantStoresField}
            rules={{ required: AdminErrorHandleFields.required.store }}
            render={() => (
              <BaseMultipleSelectorV2
                title={"Related brand(s) and store(s)"}
                groupKey="brandGroupKey"
                data={{
                  sourceData: stores,
                  targetData: watchMerchantStores,
                  targetChange: (newData) => {
                    setValue(merchantStoresField, newData, { shouldDirty: true });
                  },
                }}
                customTree={{
                  renderGroupTitle: null,
                  renderChildrenTitle: (item) => (
                    <>
                      <div>{item?.title || item?.name || "-"}</div>
                      {item?.address &&
                        <label
                          style={{
                            wordBreak: "break-word",
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            marginBottom: 0,
                          }}
                          className='tips-message'
                        >
                          {item.address}
                        </label>}
                    </>
                  ),
                }}
                loadMoreAction={() => {
                  dispatch(
                    createAction('storeModel/getPagedStoreWithBrand')({}),
                  );
                }}
                addButton={{
                  action: () => {
                    setShowCreateStorePrompt(true);
                  },
                  title: 'Create Now',
                  requires: PermissionCodes.addStore,
                  customClass:
                    'general-section-add-new btn-add-button-common',
                }}
                error={{
                  id: merchantStoresField,
                  message: AdminErrorHandleFields.required.store,
                  error: merchantStoresField in errors,
                }}
              />
            )} />
            <RedirectToStorePrompt
              show={showCreateStorePrompt}
              onHide={() => {
                setShowCreateStorePrompt(false);
              }}
              onRefresh={() => {
                setRefreshDate(new Date());
                setShowCreateStorePrompt(false);
              }}
            />
        </>
      ) : null}
      {id ? (
        <ChangePasswordCom changeAction={() => setShowChangePassPrompt(true)} />
      ) : (
        <>
          {passwordSection}
          {confirmPasswordSection}
        </>
      )}

      <ChangePasswordPrompt
        admin={{ username: adminName, pk: id }}
        show={showChangePassPrompt}
        onClose={() => setShowChangePassPrompt(false)}
        onConfirm={() => setShowChangePassPrompt(false)}
      />
    </>
  );
}

export default connect(mapPropsToState)(GeneralSections);
